import { ArmRequestError } from '@internal/plugin-azure-resources-common-react';

export type TagWrite = {
  /** Row identity, carried through to the result so the UI can match them up. */
  key: string;
  resourceGroupId: string;
  tags: Record<string, string>;
};

export type TagWriteResult = {
  key: string;
  status: 'applied' | 'failed';
  message?: string;
};

export type ApplyTagWritesOptions = {
  concurrency?: number;
  onResult?: (result: TagWriteResult) => void;
  sleep?: (ms: number) => Promise<void>;
};

const DEFAULT_CONCURRENCY = 5;
const DEFAULT_RETRY_DELAY_SECONDS = 2;
const RETRYABLE_STATUSES = new Set([429, 503]);

const defaultSleep = (ms: number) =>
  new Promise<void>(resolve => {
    setTimeout(resolve, ms);
  });

export const describeArmError = (error: unknown): string => {
  if (error instanceof ArmRequestError) {
    switch (error.status) {
      case 401:
      case 403:
        return "You don't have write access to this resource group in Azure";
      case 404:
        return 'This resource group no longer exists in Azure';
      case 429:
        return 'Azure throttled the request — retry in a moment';
      default:
        return error.message;
    }
  }
  return error instanceof Error ? error.message : 'Unknown error';
};

/**
 * Writes tags with a small worker pool so hundreds of resource groups do not hit
 * ARM all at once. Every write is reported individually — one failure never
 * cancels the rest.
 */
export const applyTagWrites = async (
  writes: TagWrite[],
  writeTags: (
    resourceGroupId: string,
    tags: Record<string, string>,
  ) => Promise<void>,
  options: ApplyTagWritesOptions = {},
): Promise<TagWriteResult[]> => {
  const {
    concurrency = DEFAULT_CONCURRENCY,
    onResult,
    sleep = defaultSleep,
  } = options;

  const results: TagWriteResult[] = [];
  let cursor = 0;

  const runOne = async (write: TagWrite): Promise<TagWriteResult> => {
    try {
      await writeTags(write.resourceGroupId, write.tags);
      return { key: write.key, status: 'applied' };
    } catch (error) {
      const retryable =
        error instanceof ArmRequestError &&
        RETRYABLE_STATUSES.has(error.status);

      if (!retryable) {
        return {
          key: write.key,
          status: 'failed',
          message: describeArmError(error),
        };
      }

      const retryAfter =
        (error as ArmRequestError).retryAfterSeconds ??
        DEFAULT_RETRY_DELAY_SECONDS;
      await sleep(retryAfter * 1000);

      try {
        await writeTags(write.resourceGroupId, write.tags);
        return { key: write.key, status: 'applied' };
      } catch (retryError) {
        return {
          key: write.key,
          status: 'failed',
          message: describeArmError(retryError),
        };
      }
    }
  };

  const worker = async () => {
    while (cursor < writes.length) {
      const write = writes[cursor];
      cursor += 1;

      const result = await runOne(write);
      results.push(result);
      onResult?.(result);
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(concurrency, writes.length) }, worker),
  );

  return results;
};
